import Task from 'models/tasks/task';
import { TenantProcessStateLibrary } from './Processes/Library/tenantProcessStateLibrary';
import { TenantProcessStateProcessSuggestions } from './Processes/ProcessSuggestions/copilotProcessSuggestion';
import { CopilotPanelContext, CopilotSubscription } from './copilotBridge';
import { CopilotFieldProposalDecision, CopilotFieldRef } from './copilotField';

// A chat answer landed. Usage-agnostic on purpose: a scene whose data the agent may have changed
// subscribes and reloads, filtering on the context and entity it cares about. afterApproval is true when
// the user approved a staged mutation; a list reloads on that only, since a plain answer changes no data.
export type CopilotGeneralCompletionHandler = (
  context: CopilotPanelContext,
  entityType?: number,
  entityId?: number,
  afterApproval?: boolean,
) => void;

// Findings of an audit were created and/or enriched by the agent.
export type CopilotAuditFindingsHandler = (internalAuditId: number, created: Task[], updated: Task[]) => void;

// The user accepted a proposed text for one entity field on a Copilot card: the editor that binds the
// field applies it, unsaved, and takes focus.
export type CopilotFieldProposalHandler = (
  ref: CopilotFieldRef,
  html: string,
  decision: CopilotFieldProposalDecision,
) => void;

export class CopilotPanelResultBus {
  private readonly annualPlanTasksHandlers = new Set<(tasks: Task[]) => void>();

  private readonly auditFindingsHandlers = new Set<CopilotAuditFindingsHandler>();

  private readonly fieldProposalHandlers = new Set<CopilotFieldProposalHandler>();

  private readonly generalCompletionHandlers = new Set<CopilotGeneralCompletionHandler>();

  private readonly libraryOrganizationHandlers = new Set<(state: TenantProcessStateLibrary) => void>();

  private readonly processSuggestionHandlers = new Set<(state: TenantProcessStateProcessSuggestions) => void>();

  // The drafting process wired a check to the annual plan (phase 27): the plan reloads its integrations.
  private readonly externalCheckWiredHandlers = new Set<(integrationId: number) => void>();

  public subscribeExternalCheckWired(handler: (integrationId: number) => void): CopilotSubscription {
    this.externalCheckWiredHandlers.add(handler);

    return new CopilotSubscription(() => {
      this.externalCheckWiredHandlers.delete(handler);
    });
  }

  public publishExternalCheckWired(integrationId: number): void {
    this.externalCheckWiredHandlers.forEach((handler) => handler(integrationId));
  }

  public subscribeAnnualPlanTasks(handler: (tasks: Task[]) => void): CopilotSubscription {
    this.annualPlanTasksHandlers.add(handler);

    return new CopilotSubscription(() => {
      this.annualPlanTasksHandlers.delete(handler);
    });
  }

  public publishAnnualPlanTasks(tasks: Task[]): void {
    this.annualPlanTasksHandlers.forEach((handler) => handler(tasks));
  }

  public subscribeAuditFindings(handler: CopilotAuditFindingsHandler): CopilotSubscription {
    this.auditFindingsHandlers.add(handler);

    return new CopilotSubscription(() => {
      this.auditFindingsHandlers.delete(handler);
    });
  }

  public publishAuditFindings(internalAuditId: number, created: Task[], updated: Task[]): void {
    this.auditFindingsHandlers.forEach((handler) => handler(internalAuditId, created, updated));
  }

  public subscribeLibraryOrganization(handler: (state: TenantProcessStateLibrary) => void): CopilotSubscription {
    this.libraryOrganizationHandlers.add(handler);

    return new CopilotSubscription(() => {
      this.libraryOrganizationHandlers.delete(handler);
    });
  }

  public publishLibraryOrganization(state: TenantProcessStateLibrary): void {
    this.libraryOrganizationHandlers.forEach((handler) => handler(state));
  }

  public subscribeProcessSuggestions(
    handler: (state: TenantProcessStateProcessSuggestions) => void,
  ): CopilotSubscription {
    this.processSuggestionHandlers.add(handler);

    return new CopilotSubscription(() => {
      this.processSuggestionHandlers.delete(handler);
    });
  }

  public publishProcessSuggestions(state: TenantProcessStateProcessSuggestions): void {
    this.processSuggestionHandlers.forEach((handler) => handler(state));
  }

  public subscribeGeneralCompletion(handler: CopilotGeneralCompletionHandler): CopilotSubscription {
    this.generalCompletionHandlers.add(handler);

    return new CopilotSubscription(() => {
      this.generalCompletionHandlers.delete(handler);
    });
  }

  public publishGeneralCompletion(
    context: CopilotPanelContext,
    entityType?: number,
    entityId?: number,
    afterApproval: boolean = false,
  ): void {
    this.generalCompletionHandlers.forEach((handler) => handler(context, entityType, entityId, afterApproval));
  }

  public subscribeFieldProposal(handler: CopilotFieldProposalHandler): CopilotSubscription {
    this.fieldProposalHandlers.add(handler);

    return new CopilotSubscription(() => {
      this.fieldProposalHandlers.delete(handler);
    });
  }

  public publishFieldProposal(ref: CopilotFieldRef, html: string, decision: CopilotFieldProposalDecision): void {
    this.fieldProposalHandlers.forEach((handler) => handler(ref, html, decision));
  }
}

export const copilotPanelResultBus = new CopilotPanelResultBus();
